import GxBlackButtonSmall from "@/app/components/common/actions/buttons/GxBlackButtonSmall";
import { IMAGE_BASE_PATH } from "@/app/utils/config";
import { getApiErrorMessage } from "@/app/utils/getApiErrorMessage";

type ErrorModule =
  | "Payment"
  | "Beneficiary"
  | "Accounts"
  | "Payer"
  | "RTP"
  | "Bulk"
  | "Invoice"
  | "BillPayment";

interface ErrorModalProp {
  open: boolean;
  errorCode?: unknown;
  module?: ErrorModule;
  close: () => void;
}

/* ----------------------------------
 * Per-module business error code maps.
 * Anything NOT found here falls through to getApiErrorMessage,
 * which resolves HTTP status (401 / 403 / 404 / 408 / 500),
 * offline, and timeout cases.
 * ----------------------------------*/
const PaymentErrors: Record<string, string> = {
  "1267": "Something went wrong while creating your secure session",
  "1269": "Your payment session has expired",
  "1006": "Auth Matrix not found",
  "1057": "Something went wrong",
  "1100": "Technical Error",
  "2FA": "Unknown 2FA",
  FGO: "Failed to generate OTP",
  OVF: "OTP verification failed",
};

const BeneficiaryErrors: Record<string, string> = {};
const AccountsErrors: Record<string, string> = {};
const BulkErrors: Record<string, string> = {
  // add Bulk/Payroll business error codes here once known
};

const MODULE_ERROR_MAPS: Partial<Record<ErrorModule, Record<string, string>>> =
  {
    Payment: PaymentErrors,
    Beneficiary: BeneficiaryErrors,
    Accounts: AccountsErrors,
    Bulk: BulkErrors,
  };

/* ----------------------------------
 * Resolve display message for any errorCode shape.
 * Mirrors the original's truthy check: "", 0, null, undefined
 * all resolve to "Technical Error" rather than a generic API message.
 * ----------------------------------*/
const resolveErrorMessage = (
  errorCode: unknown,
  module?: ErrorModule,
): string => {
  if (!errorCode) return "Technical Error";

  if (typeof errorCode === "string" || typeof errorCode === "number") {
    const map = module ? MODULE_ERROR_MAPS[module] : undefined;
    const known = map?.[String(errorCode)];
    if (known) return known;
  }

  return getApiErrorMessage(errorCode);
};

const ErrorModal = ({ open, errorCode, module, close }: ErrorModalProp) => {
  if (!open || !errorCode) return null;

  const message = resolveErrorMessage(errorCode, module);
  const isBusinessTechnicalError = module === "Payment" && errorCode === "1100";

  return (
    <div className="gx-custom-remark-modal-background-t">
      <div className="gx-custom-remark-modal-conatiner">
        <div className={`gx-custom-remark-modal ${open ? "show" : "hide"}`}>
          <main className="gx-custom-remark-modal-main">
            <div className="gx-error-image-container">
              <img src={`${IMAGE_BASE_PATH}technicalerror.svg`} alt="Warning" />
            </div>
            {!isBusinessTechnicalError ? (
              <div className="gx-error-msg-container">
                <p className="gx-p16-b">{message}</p>
              </div>
            ) : (
              <div className="gx-error-msg-container">
                <h2 className="gx-h2-26-l">
                  {"Technical"} <span className="gx-h2-26-b">{"Error"}</span>
                </h2>
              </div>
            )}
          </main>
          <footer className="gx-custom-remark-modal-footer">
            <div>
              <GxBlackButtonSmall onClick={close}>{"Close"}</GxBlackButtonSmall>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default ErrorModal;
